import { z } from "zod";

import { stripControlCharacters } from "../content-safety/unsafe-url";

/**
 * Altitude Chart app — the locked cross-layer contract.
 *
 * Three shapes live here:
 *   - `altitudeChartSettingsSchema` — the agency-wide Display settings, saved
 *     through the app store's settings endpoint.
 *   - `altitudeProfileSchema` — one tour's altitudes and its on/off switch,
 *     saved from Programme › Altitude Chart and by the AI assistant.
 *   - `altitudeChartViewSchema` — what the tour page receives, with the
 *     tour-over-app fallbacks already resolved. Absent when the chart must not
 *     show (product.md AC-08).
 *
 * Altitudes are always metres; the unit is display and input only (AC-07).
 */

export const ALTITUDE_MAX_METRES = 9000;
export const ALTITUDE_MIN_DAYS = 2;
export const ALTITUDE_MAX_DAYS = 30;
export const ALTITUDE_MAX_STOPS_PER_DAY = 10;
export const ALTITUDE_DAY_LABEL_MAX = 16;
export const ALTITUDE_PLACE_MAX = 80;
export const ALTITUDE_TITLE_MAX = 60;
export const ALTITUDE_SUBTITLE_MAX = 240;
export const FEET_PER_METRE = 3.28084;

export const altitudeUnitSchema = z.enum(["m", "ft"]);
export type AltitudeUnit = z.infer<typeof altitudeUnitSchema>;

/** Plain text only: trimmed, C0 control characters removed (AC-10). */
const plainText = (max: number) =>
  z
    .string()
    .transform((v) => stripControlCharacters(v).trim())
    .pipe(z.string().max(max));

export const altitudeChartSettingsSchema = z.object({
  title: plainText(ALTITUDE_TITLE_MAX)
    .pipe(z.string().min(1, "Add a section name"))
    .default("Altitude profile"),
  subtitle: plainText(ALTITUDE_SUBTITLE_MAX).default(
    "Every location travelers reach, day by day.",
  ),
  unit: altitudeUnitSchema.default("m"),
  showStats: z.boolean().default(true),
});
export type AltitudeChartSettings = z.infer<typeof altitudeChartSettingsSchema>;

/** Settings as the dashboard reads them: an install that was never configured,
 *  or a blob the schema cannot read, shows the defaults. */
export function parseAltitudeChartSettings(
  raw: unknown,
): AltitudeChartSettings {
  const parsed = altitudeChartSettingsSchema.safeParse(raw);
  return parsed.success ? parsed.data : altitudeChartSettingsSchema.parse({});
}

// Zod 4 numbers already reject NaN and Infinity.
const metresRange = z
  .number()
  .min(0)
  .max(ALTITUDE_MAX_METRES, `Altitudes go up to ${ALTITUDE_MAX_METRES} m`);

// Stored to one decimal (spec §5.1); a feet entry converts to long fractions.
// Only the SAVE path rounds — the view schema below stays transform-free so the
// tour DTO it sits in still serialises for the schema snapshots.
const metres = metresRange.transform((v) => Math.round(v * 10) / 10);

/**
 * A stop as saved. `place` may be empty and `alt` null only while the tour's
 * switch is off — an unfinished itinerary must never block turning the chart
 * off (AC-06). The hard caps (lengths, range, counts) hold either way.
 */
const draftStopSchema = z.object({
  place: plainText(ALTITUDE_PLACE_MAX),
  alt: metres.nullable(),
});

const draftDaySchema = z.object({
  day: plainText(ALTITUDE_DAY_LABEL_MAX),
  stops: z.array(draftStopSchema).max(ALTITUDE_MAX_STOPS_PER_DAY),
});

const stopSchema = z.object({
  place: z.string().min(1).max(ALTITUDE_PLACE_MAX),
  alt: metresRange,
});

/** A complete day — what the chart, the stats and the tour page work with. */
export const altitudeDaySchema = z.object({
  day: z.string().min(1).max(ALTITUDE_DAY_LABEL_MAX),
  stops: z.array(stopSchema).min(1).max(ALTITUDE_MAX_STOPS_PER_DAY),
});
export type AltitudeDay = z.infer<typeof altitudeDaySchema>;

export const altitudeProfileSchema = z
  .object({
    enabled: z.boolean(),
    title: plainText(ALTITUDE_TITLE_MAX),
    subtitle: plainText(ALTITUDE_SUBTITLE_MAX),
    days: z.array(draftDaySchema).max(ALTITUDE_MAX_DAYS),
  })
  .superRefine((profile, ctx) => {
    if (!profile.enabled) return;
    if (profile.days.length < ALTITUDE_MIN_DAYS) {
      ctx.addIssue({
        code: "custom",
        path: ["days"],
        message: "Add at least two days first",
      });
    }
    profile.days.forEach((day, i) => {
      const result = altitudeDaySchema.safeParse(day);
      if (result.success) return;
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: "custom",
          path: ["days", i, ...issue.path],
          message: issue.message,
        });
      }
    });
  });
export type AltitudeProfile = z.infer<typeof altitudeProfileSchema>;
export type AltitudeProfileInput = z.input<typeof altitudeProfileSchema>;

/** A tour that has never been touched: switch on, nothing typed (spec §4.1). */
export const EMPTY_ALTITUDE_PROFILE: AltitudeProfile = {
  enabled: true,
  title: "",
  subtitle: "",
  days: [],
};

/** A stored blob the schema cannot read (written before the column, or by
 *  hand) is an untouched tour: switch on, no days, so no chart. */
export function parseAltitudeProfile(raw: unknown): AltitudeProfile {
  const parsed = altitudeProfileSchema.safeParse(raw);
  return parsed.success ? parsed.data : EMPTY_ALTITUDE_PROFILE;
}

/** What `GET`/`PUT …/altitude-profile` answer with. */
export const altitudeProfileResponseSchema = z.object({
  experienceId: z.string().uuid(),
  profile: altitudeProfileSchema,
});
export type AltitudeProfileResponse = z.infer<
  typeof altitudeProfileResponseSchema
>;

export const altitudeChartViewSchema = z.object({
  title: z.string(),
  /** "" = no description line. */
  subtitle: z.string(),
  unit: altitudeUnitSchema,
  showStats: z.boolean(),
  days: z.array(altitudeDaySchema).min(ALTITUDE_MIN_DAYS),
});
export type AltitudeChartView = z.infer<typeof altitudeChartViewSchema>;

/**
 * The tour page's view, or null when no section may render (AC-08): app
 * uninstalled, switch off, or fewer than two complete days. Tour text wins
 * over app text when non-empty.
 */
export function resolveAltitudeChartView(
  settings: AltitudeChartSettings | null,
  profile: AltitudeProfile,
): AltitudeChartView | null {
  if (!settings || !profile.enabled) return null;
  const days = profile.days.filter(
    (d): d is AltitudeDay => altitudeDaySchema.safeParse(d).success,
  );
  if (days.length !== profile.days.length || days.length < ALTITUDE_MIN_DAYS) {
    return null;
  }
  return {
    title: profile.title || settings.title,
    subtitle: profile.subtitle || settings.subtitle,
    unit: settings.unit,
    showStats: settings.showStats,
    days,
  };
}

export interface AltitudeDayClimb {
  up: number;
  down: number;
}

export interface AltitudeStats {
  perDay: AltitudeDayClimb[];
  highest: { alt: number; place: string; day: string };
  totalUp: number;
  totalDown: number;
}

/**
 * Climb and drop per day (spec §5.3): each day starts where the previous one
 * ended and counts every rise and fall in travel order. Expects at least one
 * complete day.
 */
export function computeAltitudeStats(days: AltitudeDay[]): AltitudeStats {
  let last: number | null = null;
  let highest = { alt: -1, place: "", day: "" };
  const perDay = days.map((d) => {
    const climb = { up: 0, down: 0 };
    for (const stop of d.stops) {
      if (last !== null) {
        const diff = stop.alt - last;
        if (diff > 0) climb.up += diff;
        else climb.down -= diff;
      }
      last = stop.alt;
      if (stop.alt > highest.alt) {
        highest = { alt: stop.alt, place: stop.place, day: d.day };
      }
    }
    return climb;
  });
  return {
    perDay,
    highest,
    totalUp: perDay.reduce((s, c) => s + c.up, 0),
    totalDown: perDay.reduce((s, c) => s + c.down, 0),
  };
}

export const metresToUnit = (m: number, unit: AltitudeUnit): number =>
  unit === "ft" ? m * FEET_PER_METRE : m;

export const unitToMetres = (value: number, unit: AltitudeUnit): number =>
  unit === "ft" ? value / FEET_PER_METRE : value;
